/**
 * HardwareProviders — public "join the hardware pipeline" page.
 *
 * Audience: hardware manufacturers. Two parts:
 *   1. A live roster of hardware providers already on the platform — one
 *      collapsible row per provider with its device list beneath, pulled
 *      from the same public catalog API as Product.tsx. Simkura is pinned
 *      first (it's the platform operator, not just alphabetically first);
 *      everyone else follows alphabetically as they join.
 *   2. Bring your own hardware into Simkura Core (the capability-model
 *      pitch, migrated from OpenSource.tsx's old "Hardware" track).
 *
 * Reselling/white-labeling Simkura's own hardware isn't pitched here — that's
 * what the Software Integrations page already covers (a company building on
 * Simkura hardware through the API), so this page stays focused on hardware
 * providers bringing their own devices onto the platform.
 *
 * Framed per Structure.md: Simkura is building a pipeline so software
 * providers can access many different hardware providers, not just Simkura's
 * own locks — this page is the front door for a manufacturer to join that
 * pipeline, with Simkura itself as the first (and so far only) entry in the
 * roster.
 *
 * Nav + footer live in PublicLayout; this page only declares its content.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import {
  IconArrowRight,
  IconPlugConnected,
  IconChevronDown,
} from '@tabler/icons-react';
import { PublicLayout } from '../../components/PublicLayout';
import { getPublicCatalog, publicListingPath, VARIANT_STATUS_LABELS } from '../../services/listings';
import type { PublicProvider, PublicListingSummary, VariantStatus } from '../../services/listings';
import { providerLogoUrl } from '../../services/manufacturers';

const CONTACT_EMAIL = 'info@simkura.com';

// Design language shared with the other public pages: brand navy for
// accents/dark cards, steel tint for icon tiles. No purple in page content.
const NAVY = '#0d253d';
const STEEL = '#d8e4f0';

// Simkura is the platform operator — always shown first regardless of
// alphabetical order, so the roster reads "us, then everyone else."
const PLATFORM_PROVIDER_SLUG = 'simkura';

// ── Layout primitives ─────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 clamp(20px, 6vw, 64px);
`;

const Section = styled.section`
  padding: clamp(40px, 7vh, 80px) 0;
`;

// ── Hero — washed text hero, matching Home. ───────────────────────────────────

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(56px, 11vh, 120px) 0 clamp(32px, 5vh, 56px);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(60% 80% at 20% 0%, ${STEEL} 0%, transparent 60%),
      radial-gradient(50% 70% at 85% 10%, #dddcea 0%, transparent 55%),
      radial-gradient(40% 50% at 60% 100%, #c9d6df22 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Eyebrow = styled.span`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${NAVY};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const HeroHeadline = styled.h1`
  font-size: clamp(36px, 6vw, 60px);
  line-height: 1.05;
  letter-spacing: -0.025em;
  font-weight: 700;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  max-width: 18ch;
`;

const HeroSub = styled.p`
  font-size: clamp(16px, 2vw, 19px);
  line-height: 1.55;
  color: ${({ theme }) => theme.font.color.secondary};
  margin: 0;
  max-width: 52ch;
`;

// ── Section heading ──────────────────────────────────────────────────────────

const SectionLabel = styled.p`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${NAVY};
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
`;

const SectionTitle = styled.h2`
  font-size: clamp(24px, 3.4vw, 34px);
  line-height: 1.12;
  letter-spacing: -0.02em;
  font-weight: 700;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
`;

const SectionLede = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.6;
  max-width: 640px;
  margin: 0 0 ${({ theme }) => theme.spacing(8)} 0;
`;

// ── Provider roster (accordion) ───────────────────────────────────────────────

const RosterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ProviderRow = styled.div`
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  background: ${({ theme }) => theme.background.primary};
  overflow: hidden;
  scroll-margin-top: 80px;
`;

const ProviderTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(5)} ${({ theme }) => theme.spacing(6)};
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;

  .logo {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: ${STEEL};
    color: ${NAVY};
    font-weight: 700;
    font-size: 16px;
  }
  .logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .info { flex: 1; min-width: 0; }
  .name {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    font-size: 17px;
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.primary};
  }
  .overview {
    font-size: 14px;
    line-height: 1.4;
    color: ${({ theme }) => theme.font.color.secondary};
    margin-top: 2px;
  }
  .count {
    font-size: 13px;
    color: ${({ theme }) => theme.font.color.tertiary};
    margin-top: 2px;
  }
  .chev {
    flex-shrink: 0;
    display: inline-flex;
    color: ${({ theme }) => theme.font.color.tertiary};
    transition: transform 0.15s ease;
  }
  &[aria-expanded='true'] .chev { transform: rotate(180deg); }
`;

const PlatformTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: ${STEEL};
  color: ${NAVY};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const DeviceList = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.color.light};
`;

const DeviceRow = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(6)};
  text-decoration: none;
  color: inherit;
  border-top: 1px solid ${({ theme }) => theme.border.color.light};
  transition: background 0.1s ease;

  &:first-of-type { border-top: none; }
  &:hover { background: ${({ theme }) => theme.background.secondary}; }

  .name { font-size: 14px; font-weight: ${({ theme }) => theme.font.weight.medium}; color: ${({ theme }) => theme.font.color.primary}; }
  .sub { font-size: 13px; color: ${({ theme }) => theme.font.color.tertiary}; margin-top: 1px; }
  .right { display: flex; align-items: center; gap: ${({ theme }) => theme.spacing(3)}; flex-shrink: 0; }
`;

const StatusBadge = styled.span<{ tone: 'live' | 'soon' | 'off' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  white-space: nowrap;
  background: ${({ tone }) => (tone === 'live' ? '#e3f5ea' : tone === 'soon' ? '#e8f0fe' : '#f1f3f4')};
  color: ${({ tone }) => (tone === 'live' ? '#137333' : tone === 'soon' ? '#1a56db' : '#5f6368')};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const RosterEmptyState = styled.div`
  border: 1px dashed ${({ theme }) => theme.border.color.strong};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  background: ${({ theme }) => theme.background.secondary};
  padding: clamp(24px, 4vw, 40px);
  text-align: center;
  color: ${({ theme }) => theme.font.color.tertiary};
  font-size: 15px;
`;

// ── Bring your hardware — capability card ─────────────────────────────────────

const CapabilityCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: clamp(20px, 4vw, 40px);
  border-radius: ${({ theme }) => theme.border.radius.xl};
  padding: clamp(28px, 4vw, 48px);
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};

  @media (max-width: 720px) {
    flex-direction: column;
  }

  .icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${STEEL};
    color: ${NAVY};
  }
  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.65;
    color: ${({ theme }) => theme.font.color.secondary};
  }
`;

const CapabilityLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(5)};
  font-size: 15px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${NAVY};
  text-decoration: none;

  &:hover { text-decoration: underline; }
`;

// ── Closing CTA band ──────────────────────────────────────────────────────────

const CtaBand = styled.div`
  text-align: center;
  padding: clamp(44px, 7vh, 76px) 24px;
  border-radius: ${({ theme }) => theme.border.radius.xl};
  background: linear-gradient(135deg, #1b3a5e 0%, ${NAVY} 100%);
  color: #fff;

  h2 {
    font-size: clamp(26px, 4vw, 40px);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #fff;
    margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
  }
  p {
    color: rgba(255, 255, 255, 0.88);
    font-size: 18px;
    line-height: 1.55;
    margin: 0 auto ${({ theme }) => theme.spacing(7)};
    max-width: 52ch;
  }
`;

const CtaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  justify-content: center;
  align-items: center;
`;

const CtaWhite = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 52px;
  padding: 0 26px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: #fff;
  color: ${NAVY};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px); }
`;

const CtaGhostMail = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  height: 52px;
  padding: 0 22px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  border: 1px solid rgba(255, 255, 255, 0.55);
  color: #fff;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.12); }
`;

// ── Roster helpers ─────────────────────────────────────────────────────────────

function statusTone(status: VariantStatus): 'live' | 'soon' | 'off' {
  return status === 'available' ? 'live' : status === 'coming-soon' ? 'soon' : 'off';
}

function initials(name: string): string {
  return name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

function deviceCount(p: PublicProvider): number {
  return p.listings.reduce((n, l) => n + (l.variants.length || 1), 0);
}

/** Flatten a provider's listings/variants into rows for the dropdown. */
function deviceRows(listing: PublicListingSummary) {
  const boardName = listing.display_name || `${listing.provider_name} ${listing.board}`;

  if (listing.variants.length === 0) {
    return [{
      key: listing.slug,
      to: publicListingPath(listing.provider_slug, listing.slug),
      name: boardName,
      sku: listing.board,
      status: 'available' as VariantStatus,
    }];
  }
  return listing.variants.map((v) => ({
    key: `${listing.slug}/${v.slug}`,
    to: publicListingPath(listing.provider_slug, listing.slug, v.slug),
    name: v.name,
    sku: v.sku,
    status: v.status,
  }));
}

function ProviderLogo({ provider }: { provider: PublicProvider }) {
  if (provider.has_logo) {
    return <span className="logo"><img src={providerLogoUrl(provider.slug)} alt={`${provider.name} logo`} loading="lazy" /></span>;
  }
  return <span className="logo" aria-hidden="true">{initials(provider.name)}</span>;
}

// ── Page ──────────────────────────────────────────────────────────────────────

function HardwareProviders() {
  const { data: providers = [], isLoading } = useQuery({
    queryKey: ['public-catalog'],
    queryFn: getPublicCatalog,
    staleTime: 60_000,
  });

  // Simkura first, everyone else alphabetical after.
  const sorted = [...providers].sort((a, b) => {
    if (a.slug === PLATFORM_PROVIDER_SLUG) return -1;
    if (b.slug === PLATFORM_PROVIDER_SLUG) return 1;
    return a.name.localeCompare(b.name);
  });

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([PLATFORM_PROVIDER_SLUG]));
  const toggle = (slug: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  return (
    <PublicLayout>
      <HeroSection>
        <Container>
          <Hero>
            <Eyebrow>Hardware providers</Eyebrow>
            <HeroHeadline>Join the Simkura API.</HeroHeadline>
            <HeroSub>
              We're building a pipeline so software providers can access many
              different hardware providers through one platform — starting
              with Simkura's own locks. If you make access-control hardware,
              this is how you join.
            </HeroSub>
          </Hero>
        </Container>
      </HeroSection>

      {/* Provider roster — Simkura first, then everyone else as they join */}
      <Section id="providers">
        <Container>
          <SectionLabel>On the platform</SectionLabel>
          <SectionTitle>Hardware providers</SectionTitle>
          <SectionLede>
            Take a look at all the hardware providers connected to the Simkura API
          </SectionLede>

          {isLoading ? (
            <RosterEmptyState>Loading providers…</RosterEmptyState>
          ) : sorted.length === 0 ? (
            <RosterEmptyState>
              No hardware providers are listed yet. Check back soon, or{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>contact us</a>.
            </RosterEmptyState>
          ) : (
            <RosterList>
              {sorted.map((p) => {
                const isOpen = expanded.has(p.slug);
                const rows = p.listings.flatMap(deviceRows);
                return (
                  <ProviderRow key={p.slug} id={p.slug}>
                    <ProviderTrigger
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggle(p.slug)}
                    >
                      <ProviderLogo provider={p} />
                      <div className="info">
                        <span className="name">
                          {p.name}
                          {p.slug === PLATFORM_PROVIDER_SLUG && <PlatformTag>Platform</PlatformTag>}
                        </span>
                        {p.tagline && <div className="overview">{p.tagline}</div>}
                        <div className="count">{deviceCount(p)} device{deviceCount(p) === 1 ? '' : 's'}</div>
                      </div>
                      <span className="chev"><IconChevronDown size={18} /></span>
                    </ProviderTrigger>
                    {isOpen && (
                      <DeviceList>
                        {rows.map((r) => (
                          <DeviceRow key={r.key} to={r.to}>
                            <div>
                              <div className="name">{r.name}</div>
                              <div className="sub">{r.sku}</div>
                            </div>
                            <div className="right">
                              <StatusBadge tone={statusTone(r.status)}>{VARIANT_STATUS_LABELS[r.status]}</StatusBadge>
                              <IconArrowRight size={14} />
                            </div>
                          </DeviceRow>
                        ))}
                      </DeviceList>
                    )}
                  </ProviderRow>
                );
              })}
            </RosterList>
          )}
        </Container>
      </Section>

      {/* Bring your own hardware */}
      <Section id="integrate">
        <Container>
          <SectionLabel>Bring your hardware</SectionLabel>
          <SectionTitle>Join the platform as a hardware provider</SectionTitle>
          <SectionLede>
            Simkura Core is a platform for hardware providers and software providers. 
            By connecting your hardware, software providers will be able to integrate with it.
            Software providers get access to multiple panels via one API. 
          </SectionLede>
          <CapabilityCard>
            <span className="icon"><IconPlugConnected size={26} /></span>
            <div>
              <p>
                If you build hardware and firmware we would love to hear from you. 
                We give you the ablity to price the hardware and set a recurring fee if required. 
              </p>
              <CapabilityLink href={`mailto:${CONTACT_EMAIL}?subject=Simkura%20hardware%20integration`}>
                Start a hardware conversation <IconArrowRight size={15} />
              </CapabilityLink>
            </div>
          </CapabilityCard>
        </Container>
      </Section>

      {/* Closing CTA */}
      <Section>
        <Container>
          <CtaBand>
            <h2>Let's build the pipeline together.</h2>
            <p>
              Tell us about your hardware and we'll take it from there.
            </p>
            <CtaActions>
              <CtaWhite to="/signup?type=hardware">
                Create an account
                <IconArrowRight size={18} />
              </CtaWhite>
              <CtaGhostMail href={`mailto:${CONTACT_EMAIL}?subject=Simkura%20hardware%20partner%20enquiry`}>
                Talk to us first
              </CtaGhostMail>
            </CtaActions>
          </CtaBand>
        </Container>
      </Section>
    </PublicLayout>
  );
}

export default HardwareProviders;
